import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Button, Input, List, Tooltip, notification } from 'antd'
import { CloseOutlined, EditFilled, SaveFilled } from '@ant-design/icons'
import styled from 'styled-components'
import isEqual from 'lodash/isEqual'
import { colours, sizes } from '@qflow/theme'

import { ColourButton, Spinner, Text } from 'portal/lib/primitives'
import { useBeamerBarHeight, useProjectTags } from 'portal/lib/hooks'
import { MAX_TAG_LENGTH, normalizeTag } from 'portal/lib/functions'
import { projectTagCreate, projectTagRename } from 'portal/store/events/actions'
import { trackMixpanel } from 'portal/store/mixpanel/actions'

import { ProjectsData, TitleBar, RightSide } from './SettingsStyledComponents'

export const Tags = ({ project }: TagsProps) => {
  const dispatch = useDispatch()
  const { accountId } = useParams<{ accountId: string }>()
  // Pass projectId explicitly rather than letting useProjectTags default to
  // the route: Tags also renders from Account Settings' Tag Management tab,
  // where the project comes from state
  const { projectTags, loading, error } = useProjectTags(project.projectId)
  const tagRename = useSelector((state: any) => state.events.tagRename)
  const tagCreate = useSelector((state: any) => state.events.tagCreate)

  // Tags are a set: collapse any duplicate strings the API returns before
  // they become rows, so two rows never show the same tag text.
  const uniqueTags = useMemo(() => [...new Set(projectTags)], [projectTags])

  // Only one row can be in edit mode at a time.
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [draftValue, setDraftValue] = useState('')

  // Cap the tag list's height to the viewport so it scrolls internally
  // instead of growing the whole page, matching ProjectSelector.tsx's
  // approach - including accounting for the BeamerBar, if present. The base
  // offset is bigger than ProjectSelector's 242px because Tags renders
  // inside an extra layer ProjectSelector doesn't have: ProjectsData's own
  // padding plus its TitleBar (the project name heading) sit above
  // SectionContainer here.
  const tableMaxHeight = `calc(100vh - ${332 + useBeamerBarHeight()}px)`

  const startEditing = (index: number) => {
    setEditingIndex(index)
    setDraftValue(uniqueTags[index])
  }

  const cancelEditing = () => {
    setEditingIndex(null)
    setDraftValue('')
  }

  // Whether the row currently being edited has unsaved changes. Used to
  // decide if switching to another row is safe to do implicitly (same as
  // Cancel + Edit) versus requiring an explicit Cancel/Save first.
  const isCurrentEditDirty =
    editingIndex !== null &&
    normalizeTag(draftValue).length > 0 &&
    !isEqual(normalizeTag(draftValue), uniqueTags[editingIndex])

  const canSwitchAwayFromCurrentEdit =
    editingIndex === null || (!isCurrentEditDirty && !tagRename.loading)

  const handleSaveEdit = () => {
    if (editingIndex === null) {
      return
    }

    const originalTagName = uniqueTags[editingIndex]
    const updatedTagName = normalizeTag(draftValue)

    if (!updatedTagName || updatedTagName === originalTagName) {
      return
    }

    // Duplicate check - catch a collision with a different
    // existing tag before it reaches the backend, whose
    // response for this case isn't fit to show to the user.
    if (uniqueTags.includes(updatedTagName)) {
      notification.error({
        message: 'Tag already exists',
        description: `A tag named "${updatedTagName}" already exists for this project.`
      })
      return
    }

    dispatch(
      projectTagRename(
        accountId,
        project.projectId,
        originalTagName,
        updatedTagName,
        () => {
          dispatch(
            trackMixpanel('frontend_project_tag_updated', {
              account_id: accountId,
              project_id: project.projectId,
              original_tag_name: originalTagName,
              new_tag_name: updatedTagName
            })
          )
          cancelEditing()
        },
        () => {
          notification.error({
            message: 'Unable to update tag',
            description:
              'Something went wrong updating this tag. Please try again.'
          })
        }
      )
    )
  }

  const [newTagValue, setNewTagValue] = useState('')
  const isNewTagValid = normalizeTag(newTagValue).length > 0

  const handleCreateSave = () => {
    const newTagName = normalizeTag(newTagValue)

    if (!newTagName) {
      return
    }

    // Duplicate check - catch a collision with a different
    // existing tag before it reaches the backend, whose
    // response for this case isn't fit to show to the user.
    if (uniqueTags.includes(newTagName)) {
      notification.error({
        message: 'Tag already exists',
        description: `A tag named "${newTagName}" already exists for this project.`
      })
      return
    }

    dispatch(
      projectTagCreate(
        accountId,
        project.projectId,
        newTagName,
        () => {
          dispatch(
            trackMixpanel('frontend_project_tag_added', {
              account_id: accountId,
              project_id: project.projectId,
              tag_name: newTagName
            })
          )
          setNewTagValue('')
        },
        () => {
          notification.error({
            message: 'Unable to create tag',
            description:
              'Something went wrong creating this tag. Please try again.'
          })
        }
      )
    )
  }

  if (loading) {
    return <Spinner />
  }

  if (error) {
    return <span>An error has occurred. Please try refreshing the page</span>
  }

  return (
    <ProjectsData id="tags-project-data">
      <TitleBar>
        <h3>{project.name}</h3>
      </TitleBar>
      <SectionContainer>
        <Section>
          <h4>Add new tag</h4>
          <AddTagRow>
            <Input
              aria-label="New tag"
              placeholder="Tag name"
              value={newTagValue}
              maxLength={MAX_TAG_LENGTH}
              disabled={tagCreate.loading}
              onChange={e => {
                const rawValue = e.target.value
                setNewTagValue(
                  rawValue
                    .toLowerCase()
                    .replace(/\//g, '')
                    .slice(0, MAX_TAG_LENGTH)
                )
              }}
              onBlur={() => setNewTagValue(prev => normalizeTag(prev))}
              onPressEnter={() => {
                if (isNewTagValid && !tagCreate.loading) {
                  handleCreateSave()
                }
              }}
            />
            <Button
              type="primary"
              disabled={!isNewTagValid || tagCreate.loading}
              onClick={handleCreateSave}
            >
              Save
            </Button>
          </AddTagRow>
          <Text size={sizes.X_SMALL}>
            max. {MAX_TAG_LENGTH} characters per tag
          </Text>
        </Section>
        <Section $maxHeight={tableMaxHeight}>
          <h4>Existing tags</h4>
          <List
            size="small"
            bordered
            style={{
              marginTop: '0.75rem',
              backgroundColor: colours.WHITE,
              overflow: 'hidden'
            }}
            dataSource={uniqueTags}
            locale={{ emptyText: 'No tags added yet.' }}
            renderItem={(tag: string, index: number) => {
              const isEditing = editingIndex === index
              const normalizedDraft = normalizeTag(draftValue)
              const isDirty =
                isEditing &&
                normalizedDraft.length > 0 &&
                !isEqual(normalizedDraft, tag)

              // Clicking anywhere on a row starts editing it, same as the
              // Edit button. Never re-trigger on the row already being
              // edited (that would wipe the draft) - but switching to a
              // different row is allowed as long as the current edit has no
              // unsaved changes, same as clicking Cancel first would do.
              const canStartEditing = !isEditing && canSwitchAwayFromCurrentEdit

              return (
                <List.Item style={{ padding: '0' }}>
                  <DataWrapper
                    id={`tag-row-${index}`}
                    $clickable={canStartEditing}
                    onClick={() => {
                      if (canStartEditing) {
                        startEditing(index)
                      }
                    }}
                  >
                    {isEditing ? (
                      <EditInputWrapper>
                        <Input
                          aria-label={`Tag ${index + 1}`}
                          value={draftValue}
                          maxLength={MAX_TAG_LENGTH}
                          disabled={tagRename.loading}
                          onChange={e => {
                            const rawValue = e.target.value
                            setDraftValue(
                              rawValue
                                .toLowerCase()
                                .replace(/\//g, '')
                                .slice(0, MAX_TAG_LENGTH)
                            )
                          }}
                          onBlur={() =>
                            setDraftValue(prev => normalizeTag(prev))
                          }
                          onPressEnter={() => {
                            if (isDirty && !tagRename.loading) {
                              handleSaveEdit()
                            }
                          }}
                          autoFocus
                        />
                      </EditInputWrapper>
                    ) : (
                      <Tooltip title={tag}>
                        <TagText>{tag}</TagText>
                      </Tooltip>
                    )}
                    <RightSide>
                      {isEditing ? (
                        <>
                          <Tooltip title="Cancel">
                            <ColourButton.BasicBorderless
                              aria-label="Cancel"
                              style={{
                                padding: '4px 9px',
                                borderRadius: '50%'
                              }}
                              disabled={tagRename.loading}
                              onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                cancelEditing()
                              }}
                            >
                              <CloseOutlined
                                style={{
                                  fontSize: sizes.MEDIUM,
                                  color: colours.CLOUD_GREY
                                }}
                              />
                            </ColourButton.BasicBorderless>
                          </Tooltip>
                          <Tooltip
                            title={isDirty ? 'Save tag' : 'No changes to save'}
                          >
                            <ColourButton.BasicBorderless
                              aria-label="Save"
                              style={{
                                padding: '4px 9px',
                                borderRadius: '50%'
                              }}
                              disabled={!isDirty || tagRename.loading}
                              onClick={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleSaveEdit()
                              }}
                            >
                              <SaveFilled
                                style={{
                                  fontSize: sizes.MEDIUM,
                                  color: isDirty
                                    ? colours.TEAL_HOVER
                                    : colours.CLOUD_GREY
                                }}
                              />
                            </ColourButton.BasicBorderless>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit tag">
                          <ColourButton.BasicBorderless
                            aria-label="Edit"
                            style={{ padding: '4px 9px', borderRadius: '50%' }}
                            disabled={!canStartEditing}
                            onClick={e => {
                              e.preventDefault()
                              e.stopPropagation()
                              startEditing(index)
                            }}
                          >
                            <EditFilled
                              style={{
                                fontSize: sizes.MEDIUM,
                                color: colours.CORE_NAVY
                              }}
                            />
                          </ColourButton.BasicBorderless>
                        </Tooltip>
                      )}
                    </RightSide>
                  </DataWrapper>
                </List.Item>
              )
            }}
          />
        </Section>
      </SectionContainer>
    </ProjectsData>
  )
}

type TagsProps = {
  project: ProjectSettings
  onHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void
}

export interface ProjectSettings {
  name: string
  projectId: string
}

const DataWrapper = styled.div<{ $clickable?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 0.25rem 0.25rem 0.625rem;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
`

const TagText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Section = styled.div<{ $maxHeight?: string }>`
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 0.625rem;
  padding: 1rem 1.5rem 1.5rem 1.5rem;
  margin-bottom: 0.625rem;
  width: 100%;
  height: fit-content;
  max-height: ${props => props.$maxHeight ?? 'none'};
  overflow-y: auto;

  & > h4 {
    color: ${colours.CORE_NAVY};
    font-family: 'Jakarta Sans', 'Inter', sans-serif !important;
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
`

const AddTagRow = styled.div`
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const EditInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 0.125rem;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`
